import React, { useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { usePermissions } from '../../hooks/usePermissions';
import {
  selectAllReviews,
  clearSelection,
  bulkGenerateAiReplies,
  BULK_GENERATE_MAX,
} from '../../store/slices/googleReviewSlice';

const BULK_BAR_EXTRA_SPACING_PX = 5;

const BulkActionsBar: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { selectedReviewIds, reviews, isGenerating, selectedLocationId } = useAppSelector((s) => s.googleReview);
  // Subuser: bulk generation requires google_review_write.
  const { can } = usePermissions();
  const canWrite = can('google_review_write');
  const outerRef = useRef<HTMLDivElement>(null);
  const [spacerHeight, setSpacerHeight] = useState(0);

  const count = selectedReviewIds.length;
  // `reviews` grows with every "Load more", so a full selection is capped at
  // BULK_GENERATE_MAX rather than at reviews.length.
  const selectableCount = Math.min(reviews.length, BULK_GENERATE_MAX);
  const allSelected = count >= selectableCount && selectableCount > 0;

  useLayoutEffect(() => {
    if (count === 0) {
      setSpacerHeight(0);
      return;
    }

    const outer = outerRef.current;
    if (!outer) return;

    const updateSpacer = () => {
      const bar = outer.querySelector('.bulk-actions-bar');
      if (!bar) return;

      const barHeight = bar.getBoundingClientRect().height;
      const bottomOffset = parseFloat(getComputedStyle(outer).bottom) || 0;
      setSpacerHeight(barHeight + bottomOffset + BULK_BAR_EXTRA_SPACING_PX);
    };

    updateSpacer();

    const bar = outer.querySelector('.bulk-actions-bar');
    if (!bar) return;

    const resizeObserver = new ResizeObserver(updateSpacer);
    resizeObserver.observe(bar);
    window.addEventListener('resize', updateSpacer);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateSpacer);
    };
  }, [count, isGenerating]);

  const handleGenerate = async () => {
    if (!selectedLocationId) {
      return;
    }

    try {
      await dispatch(bulkGenerateAiReplies(selectedReviewIds)).unwrap();
      navigate('/features/google-review-manager/bulk-review');
    } catch {
      // Error notification is handled by the slice
    }
  };

  if (count === 0) return null;

  return (
    <>
      <div
        className="bulk-actions-bar__spacer"
        style={{ height: spacerHeight }}
        aria-hidden="true"
      />
      <div className="bulk-actions-bar__outer" ref={outerRef} aria-live="polite">
        <div className="bulk-actions-bar" role="status">

          <span className="bulk-actions-bar__count">
            {t('google_review_bulk_selected_of_max', { count, max: BULK_GENERATE_MAX })}
            <span className="bulk-actions-bar__max-hint">
              {t('google_review_bulk_max_hint', { max: BULK_GENERATE_MAX })}
            </span>
          </span>

          <span className="bulk-actions-bar__sep" aria-hidden="true" />

          <button
            className="bulk-actions-bar__generate-btn"
            onClick={handleGenerate}
            disabled={isGenerating || !selectedLocationId || !canWrite}
            title={!canWrite ? t('subuser_no_permission_tooltip') : undefined}
          >
            {isGenerating ? (
              <>
                <span className="bulk-actions-bar__spinner" aria-hidden="true" />
                {t('google_review_bulk_generating')}
              </>
            ) : (
              <>✨ {t('google_review_bulk_generate')}</>
            )}
          </button>

          <button
            className="bulk-actions-bar__select-all-btn"
            onClick={() => dispatch(allSelected ? clearSelection() : selectAllReviews())}
            disabled={isGenerating}
          >
            {allSelected ? (
              <>{t('google_review_bulk_deselect_all')}</>
            ) : (
              <>✓ {t('google_review_bulk_select_all')}</>
            )}
          </button>

          <span className="bulk-actions-bar__sep" aria-hidden="true" />

          <button
            className="bulk-actions-bar__cancel-btn"
            onClick={() => dispatch(clearSelection())}
            disabled={isGenerating}
          >
            × {t('google_review_bulk_cancel')}
          </button>

        </div>
      </div>
    </>
  );
};

export default BulkActionsBar;
